import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {  BadgeInfo, CreditCard } from "lucide-react";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Spin } from "antd";
import { StripePaymentElementOptions } from "@stripe/stripe-js";
import PAGES from "routes/constants";
import { getPromoCode as getStoredPromoCode, getSignupEmail, setPromoCode as setStoredPromoCode, setSignupEmail } from "services/storages/userStorage";
import API from "services/api";
import useCommonStore from "stores/useCommon";

const DEFAULT_PROMO_CODE_ERROR = "Veuillez rentrer un promo code valide.";

interface PaymentFormProps {
  amount: number | undefined;
  appliedPromoCode?: string;
  onAddPromoCode: (code: string, discountPercent: number) => void;
  onRemovePromoCode: () => void;
  clientSecret: string;
  offerName?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  appliedPromoCode,
  onAddPromoCode,
  onRemovePromoCode,
  clientSecret,
  offerName,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const { setAlertTop, closeAlertTop } = useCommonStore();

  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [signupEmail, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  // This component remounts whenever the clientSecret changes, which happens every
  // time the price moves. Seed from what the user typed (storage, cleared by the
  // parent on page entry) and trust the parent for whether that code is applied.
  const [promoCode, setPromoCode] = useState(
    getStoredPromoCode() || appliedPromoCode || ""
  );
  const [isValidPromoCode, setIsValidPromoCode] = useState(
    !!appliedPromoCode && getStoredPromoCode() === appliedPromoCode
  );
  const [promoCodeError, setPromoCodeError] = useState("");
  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false);
  // Only the latest validation request may apply a code
  const validationSeq = useRef(0);

  useEffect(() => {
    const savedEmail = getSignupEmail();
    if (savedEmail) {
      setEmail(savedEmail);
      setIsEmailValid(validateEmail(savedEmail));
    }

    // The parent already clears the stored code when the page is entered; clearing
    // again here would blank the field on every remount while the price stays
    // discounted.
  }, []);


  const { mutateAsync: mutateValidate, isPending } = useMutation({
    mutationFn: (params: any) => API.mock.codeValidate(params),
  });

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    setIsEmailValid(validateEmail(newEmail));
    setSignupEmail(newEmail);
  };

  const handlePromoCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPromoCode = e.target.value;
    setPromoCode(newPromoCode);
    setStoredPromoCode(newPromoCode);
    setPromoCodeError("");
    // Only one code can be active. Mark it unapplied for the UI, but leave the price
    // alone until validation resolves: changing it here would remount this component
    // and cancel the pending validation. The parent always applies the percentage to
    // the undiscounted price, so a replacement cannot stack.
    setIsValidPromoCode(false);
    validatePromoCodeDebounce(newPromoCode, signupEmail);
  };

  const handleValidatePromoCode = useCallback(async (newPromoCode: string, emailForPayload?: string) => {
    if (newPromoCode.length === 8) {
      const seq = ++validationSeq.current;
      try {
        const result = await mutateValidate({
          ref_ademe: location.state?.refAdeme,
          code: newPromoCode,
          email: (emailForPayload || signupEmail || getSignupEmail() || "").trim(),
          ...(offerName ? { offer_name: offerName } : {}),
        });

        // A newer code was submitted while this request was in flight
        if (seq !== validationSeq.current) {
          return;
        }

        if (result?.status === 0) {
          setIsValidPromoCode(true);
          setPromoCodeError("");
          onAddPromoCode(newPromoCode, result?.discount_percent);
          setAlertTop({
            type: "info",
            message: `votre code promo ${newPromoCode} est appliqué avec succès`,
            closeable: true,
          });
          setTimeout(() => {
            closeAlertTop();
          }, 5000);
        } else {
          setIsValidPromoCode(false);
          onRemovePromoCode();
          // the API returns a reason on rejection; fall back for older responses
          setPromoCodeError(result?.text || DEFAULT_PROMO_CODE_ERROR);
        }
      } catch (error) {
        console.error("Error validating promo code:", error);
        if (seq === validationSeq.current) {
          setIsValidPromoCode(false);
          onRemovePromoCode();
          setPromoCodeError(DEFAULT_PROMO_CODE_ERROR);
        }
      }
    } else {
      validationSeq.current += 1;
      onRemovePromoCode();
      setIsValidPromoCode(false);
      setPromoCodeError("");
    }
  }, [closeAlertTop, location.state?.refAdeme, mutateValidate, offerName, onAddPromoCode, onRemovePromoCode, setAlertTop, signupEmail]);

  const validatePromoCodeDebounce = useMemo(
    () =>
      _.debounce((code: string, emailArg?: string) => {
        handleValidatePromoCode(code, emailArg);
      }, 1000),
    [handleValidatePromoCode]
  );

  useEffect(() => {
    return () => {
      validatePromoCodeDebounce.cancel();
    };
  }, [validatePromoCodeDebounce]);

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setSignupEmail(signupEmail);

    setIsLoading(true);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setIsLoading(false);
      setMessage(submitError.message || "Veuillez vérifier vos informations de paiement.");
      return;
    }

    let error: any = null;
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/#${PAGES.paymentComplete}`,
          receipt_email: signupEmail,
        },
      });
      error = result?.error;
    } catch (e: any) {
      error = e;
    }

    if (error.type === "card_error" || error.type === "validation_error") {
      console.error(error.message || message);
      setMessage(error.message || "An unexpected error occurred.");
    } else {
      console.error("An unexpected error occurred.");
      setMessage("An unexpected error occurred.");
    }

    setIsLoading(false);
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: "accordion",
  };

  return (
    <form
      id="payment-form"
      onSubmit={handleSubmit}

    >
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <BadgeInfo className="w-6 h-6 package-icon mr-2" />
          <h2 className="text-xl font-bold text-gray-800">
            Informations
          </h2>
        </div>
        <div className="email-input-container mb-4">
          <label
            htmlFor="email-input"
            className="block text-left text-sm font-medium text-gray-700 mb-2"
          >
            Veuillez saisir votre adresse email
          </label>
          <input
            id="email-input"
            type="email"
            value={signupEmail}
            onChange={handleEmailChange}
            className={`w-full p-3 border ${
              !isEmailValid && signupEmail ? "border-red-500" : "border-gray-300"
            } rounded-md`}
            placeholder="Email"
            required
          />
          {!isEmailValid && signupEmail && (
            <p className="text-red-500 text-sm mt-1">
              Veuillez rentrer un email valide.
            </p>
          )}
          <p className="text-sm mt-1">Votre mail permettra d’accéder instantanément à votre tableau de bord et vous recevrez dans les 24h votre rapport fiabilité au format pdf</p>

        </div>
        <div className="h-4" />

        <div className="email-input-container mb-4">
          <label
            htmlFor="email-input"
            className="block text-left text-sm font-medium text-gray-700 mb-2"
          >
            Je dispose d’un code promo
          </label>
          <div className="flex items-center relative">
            <input
              id="promo-input"
              type="text"
              value={promoCode}
              onChange={handlePromoCodeChange}
              className={`w-full p-3 border !pr-12 bg-white ${
                !isEmailValid && signupEmail
                  ? "border-red-500"
                  : "border-gray-300"
              } rounded-md`}
              placeholder="Code"
            />

            {isValidPromoCode && !isPending && (
              <CheckOutlined className="text-[18px] absolute right-4 top-1/2 transform -translate-y-1/2 *:fill-[#15595a]" />
            )}
            {isPending && (
              <Spin
                style={{ fontSize: "24px" }}
                indicator={<LoadingOutlined />}
                className="text-[18px] absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center justify-center"
              />
            )}
          </div>

          {!isValidPromoCode && promoCode && (
            <p className="text-red-500 text-sm mt-1">
              {promoCodeError || DEFAULT_PROMO_CODE_ERROR}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center mb-4">
        <CreditCard className="w-6 h-6 package-icon mr-2" />
        <h2 className="text-xl font-bold text-gray-800">
          Paiement
        </h2>
      </div>
      


      <label className="block text-left text-sm font-medium text-gray-700 mb-2">
        Veuillez saisir vos informations de carte bancaire
      </label>
      <PaymentElement id="payment-element" options={paymentElementOptions} onReady={() => setIsPaymentElementReady(true)} />
      <input
        type="checkbox"
        id="terms"
        checked={isTermsAccepted}
        onChange={(e) => setIsTermsAccepted(e.target.checked)}
        className="mr-2"
      />
      <label htmlFor="terms" className="text-sm text-gray-600">
        En cochant cette case vous acceptez les{" "}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 hover:text-blue-700 underline"
        >
          conditions générales de vente et d'utilisation
        </a>
        .
      </label>
      <button
        disabled={
          isLoading ||
          !stripe ||
          !elements ||
          !isPaymentElementReady ||
          !signupEmail ||
          !isEmailValid ||
          !isTermsAccepted
        }
        id="submit"
        className="stripe-button"
      >
        <span id="button-text">
          {isLoading ? (
            <div className="spinner" id="spinner"></div>
          ) : (
            `Payer ${typeof amount === "number" ? amount.toFixed(2) : ""} €`
          )}
        </span>
      </button>
      </div>
    </form>
  );
};

export default PaymentForm;
